import type { IWorkStream } from '../types/WorkStream';
import { sortByDate } from './timestampUtils';

/** Sentinel key for streams with no named project (general company / living-wage work). */
export const GENERAL_COMPANY_PROJECT_KEY = '__general__';

/** Stable within-group order: createdAt ascending, then id (ignores updatedAt bumps). */
function compareWorkStreamsForBoard(a: IWorkStream, b: IWorkStream): number {
  const byCreated = sortByDate<IWorkStream>((ws) => ws.createdAt, 'asc')(a, b);
  if (byCreated !== 0) return byCreated;
  return (a.id || '').localeCompare(b.id || '');
}

export const GENERAL_COMPANY_PROJECT_LABEL = 'General company work';

export type WorkStreamProjectGroup = {
  key: string;
  name: string;
  isGeneral: boolean;
  workStreams: IWorkStream[];
};

/** Trimmed project name, or null when the stream belongs to general company work. */
export function normaliseWorkStreamProjectName(
  projectName?: unknown,
): string | null {
  const trimmed = typeof projectName === 'string' ? projectName.trim() : '';
  return trimmed.length > 0 ? trimmed : null;
}

export function getWorkStreamProjectKey(projectName?: string | null): string {
  return normaliseWorkStreamProjectName(projectName) ?? GENERAL_COMPANY_PROJECT_KEY;
}

export function getWorkStreamProjectLabel(projectName?: string | null): string {
  return normaliseWorkStreamProjectName(projectName) ?? GENERAL_COMPANY_PROJECT_LABEL;
}

export function isGeneralCompanyWorkStream(
  workStream: Pick<IWorkStream, 'projectName'>,
): boolean {
  return normaliseWorkStreamProjectName(workStream.projectName) === null;
}

/**
 * Distinct named project labels already used on work streams (excludes General).
 * Sorted case-insensitively for editor autocomplete.
 * Prefer listBudgetTrackerProjectNames when budget trackers are the source of truth.
 */
export function listKnownWorkStreamProjectNames(
  workStreams: Array<Pick<IWorkStream, 'projectName'>>,
): string[] {
  const names = new Set<string>();
  for (const stream of workStreams) {
    const name = normaliseWorkStreamProjectName(stream.projectName);
    if (name) names.add(name);
  }
  return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/**
 * Merge stream groups with budget-tracker project names so empty trackers
 * still appear as board projects.
 */
export function groupWorkStreamsByProjectWithTrackers(
  workStreams: IWorkStream[],
  trackerProjectNames: string[],
): WorkStreamProjectGroup[] {
  const groups = groupWorkStreamsByProject(workStreams);
  const existingKeys = new Set(groups.map((g) => g.key.toLowerCase()));
  const extras: WorkStreamProjectGroup[] = [];

  for (const rawName of trackerProjectNames) {
    const name = normaliseWorkStreamProjectName(rawName);
    if (!name) continue;
    if (existingKeys.has(name.toLowerCase())) continue;
    extras.push({
      key: name,
      name,
      isGeneral: false,
      workStreams: [],
    });
    existingKeys.add(name.toLowerCase());
  }

  if (extras.length === 0) return groups;

  const named = [...groups.filter((g) => !g.isGeneral), ...extras].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );
  const general = groups.find((g) => g.isGeneral);
  return general ? [...named, general] : named;
}

/**
 * Work streams that currently use this board project name (case-insensitive trimmed match).
 */
export function getWorkStreamsForProjectName(
  workStreams: Array<Pick<IWorkStream, 'id' | 'projectName'>>,
  projectName: string,
): Array<Pick<IWorkStream, 'id' | 'projectName'>> {
  const target = normaliseWorkStreamProjectName(projectName)?.toLowerCase();
  if (!target) return [];
  return workStreams.filter(
    (stream) =>
      normaliseWorkStreamProjectName(stream.projectName)?.toLowerCase() === target,
  );
}

/**
 * Group work streams by board project.
 * Named projects first (A–Z), then General company work last.
 */
export function groupWorkStreamsByProject(
  workStreams: IWorkStream[],
): WorkStreamProjectGroup[] {
  const byKey = new Map<string, WorkStreamProjectGroup>();

  for (const stream of workStreams) {
    const name = normaliseWorkStreamProjectName(stream.projectName);
    const key = name ?? GENERAL_COMPANY_PROJECT_KEY;
    const existing = byKey.get(key);
    if (existing) {
      existing.workStreams.push(stream);
      continue;
    }
    byKey.set(key, {
      key,
      name: name ?? GENERAL_COMPANY_PROJECT_LABEL,
      isGeneral: name === null,
      workStreams: [stream],
    });
  }

  for (const group of byKey.values()) {
    group.workStreams.sort(compareWorkStreamsForBoard);
  }

  const named = [...byKey.values()]
    .filter((group) => !group.isGeneral)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

  const general = byKey.get(GENERAL_COMPANY_PROJECT_KEY);
  return general ? [...named, general] : named;
}
